import type {
  ActivationCodeView,
  AdminNavCounts,
  AppConfigView,
  AppMode,
  AiEventView,
  AiModelsView,
  AuditLogView,
  CampaignDetailView,
  CampaignsView,
  ChatReplyView,
  ConversationSummaryView,
  ConversationView,
  FacebookSettingsView,
  MarketingSettingsView,
  MarketingUsageView,
  JobRunDetailView,
  JobRunResult,
  JobRunsView,
  JobsConfig,
  JobType,
  MembershipAdminView,
  Reward,
  Tier,
  TierPolicy,
  ApiErrorBody,
  AuthResult,
  BranchInput,
  CatalogView,
  CounterEarnResult,
  CounterOrderInput,
  CounterReceiptsView,
  CounterRedeemLookupView,
  CounterRedeemResult,
  CounterRedemptionsView,
  CustomerDetailView,
  CustomersAdminView,
  DashboardView,
  DeliveryFeeBand,
  EmployeeView,
  LoyaltyRules,
  LoyaltyView,
  MenuAdminView,
  MeView,
  OrderBoardView,
  OrderView,
  OtpRequested,
  PaymentsAdminView,
  PhoneStatusView,
  PayrollView,
  Permission,
  PlaceOrderInput,
  PlaceOrderResult,
  ProductInput,
  ProductView,
  PromotionInput,
  PromotionView,
  PromotionsAdminView,
  QuoteInput,
  QuoteView,
  SettingsAdminView,
  ShipperApplicationInput,
  ShipperEarningsView,
  ShipperHomeView,
  ShipperProfileView,
  SessionUser,
  ShipperSignupOptions,
  ShippersAdminView,
  StaffRole,
  StockStatus,
  TimesheetView,
  UiPrefs,
} from '@khaddi/contracts';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly issues: { path: string; message: string }[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export type ApiClientOptions = {
  /** Origin of the API, '' for same-origin (nginx routes /api to the API service). */
  baseUrl?: string;
  getToken: () => string | null;
  /** Called on 401 so the app can drop the session and show login. */
  onUnauthorized?: () => void;
  fetchImpl?: typeof fetch;
};

type Query = Record<string, string | number | undefined | null>;

const toQuery = (query?: Query): string => {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
};

export const createApiClient = ({ baseUrl = '', getToken, onUnauthorized, fetchImpl }: ApiClientOptions) => {
  const doFetch = fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));

  const request = async <T>(method: string, path: string, options: { body?: unknown; query?: Query } = {}): Promise<T> => {
    const token = getToken();
    const response = await doFetch(`${baseUrl}/api${path}${toQuery(options.query)}`, {
      method,
      headers: {
        ...(options.body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });
    if (response.status === 204) return undefined as T;
    const data = (await response.json().catch(() => null)) as unknown;
    if (!response.ok) {
      if (response.status === 401) onUnauthorized?.();
      const error = (data as ApiErrorBody | null)?.error;
      throw new ApiError(response.status, error?.code ?? 'HTTP_ERROR', error?.message ?? `Lỗi ${response.status}`, error?.issues);
    }
    return data as T;
  };

  const get = <T>(path: string, query?: Query) => request<T>('GET', path, { query });
  const post = <T>(path: string, body: unknown = {}) => request<T>('POST', path, { body });
  const put = <T>(path: string, body: unknown) => request<T>('PUT', path, { body });
  const del = <T>(path: string) => request<T>('DELETE', path);

  /** Absolute URL for downloads (CSV) that must carry the token as a header — use with `download()`. */
  const download = async (path: string, query?: Query): Promise<Blob> => {
    const token = getToken();
    const response = await doFetch(`${baseUrl}/api${path}${toQuery(query)}`, { headers: token ? { authorization: `Bearer ${token}` } : {} });
    if (!response.ok) throw new ApiError(response.status, 'HTTP_ERROR', `Lỗi ${response.status}`);
    return response.blob();
  };

  /**
   * POSTs and reads a server-sent-event stream: `progress` events go to `onProgress`, `done` resolves, `error` rejects.
   * Used by the AI chat, whose answers take 10–60 seconds.
   */
  const postStream = async <T, P>(path: string, payload: unknown, onProgress: (event: P) => void): Promise<T> => {
    const token = getToken();
    const response = await doFetch(`${baseUrl}/api${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'text/event-stream', ...(token ? { authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(payload),
    });
    if (!response.ok || !response.body) {
      const data = (await response.json().catch(() => null)) as ApiErrorBody | null;
      if (response.status === 401) onUnauthorized?.();
      throw new ApiError(response.status, data?.error?.code ?? 'HTTP_ERROR', data?.error?.message ?? `Lỗi ${response.status}`, data?.error?.issues);
    }
    const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
    let buffer = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += value;
      let split: number;
      while ((split = buffer.indexOf('\n\n')) >= 0) {
        const raw = buffer.slice(0, split);
        buffer = buffer.slice(split + 2);
        const event = /^event: (.*)$/m.exec(raw)?.[1]?.trim() ?? 'message';
        const data = raw.split('\n').filter((l) => l.startsWith('data: ')).map((l) => l.slice(6)).join('\n');
        if (!data) continue;
        const parsed: unknown = JSON.parse(data);
        if (event === 'progress') onProgress(parsed as P);
        else if (event === 'done') return parsed as T;
        else if (event === 'error') throw new ApiError(400, 'AI_ERROR', (parsed as { message?: string }).message ?? 'Lỗi trợ lý AI');
      }
    }
    throw new ApiError(500, 'STREAM_ENDED', 'Kết nối bị ngắt trước khi trợ lý trả lời xong');
  };

  return {
    auth: {
      requestOtp: (phone: string) => post<OtpRequested>('/auth/otp/request', { phone }),
      verifyOtp: (input: { phone: string; code: string; name?: string; app: 'client' | 'shipper' }) => post<AuthResult>('/auth/otp/verify', input),
      staffLogin: (email: string, password: string) => post<AuthResult>('/auth/staff/login', { email, password }),
      phoneStatus: (phone: string) => post<PhoneStatusView>('/auth/phone/status', { phone }),
      registerWithPin: (input: { phone: string; name: string; pin: string; activationCode?: string }) => post<AuthResult>('/auth/pin/register', input),
      loginWithPin: (phone: string, pin: string) => post<AuthResult>('/auth/pin/login', { phone, pin }),
      resetPin: (input: { phone: string; activationCode: string; pin: string }) => post<AuthResult>('/auth/pin/reset', input),
      setPin: (pin: string) => post<SessionUser>('/auth/pin/set', { pin }),
      verifyWithCode: (code: string) => post<SessionUser>('/auth/verify', { code }),
      showStaffCode: () => post<ActivationCodeView>('/auth/verify/show-code'),
      me: () => get<MeView>('/auth/me'),
      updatePrefs: (prefs: UiPrefs) => put<{ user: SessionUser }>('/auth/prefs', prefs),
      updateProfile: (input: { name: string; birthday?: string | null }) => put<MeView>('/me/profile', input),
    },

    appConfig: () => get<AppConfigView>('/app-config'),

    catalog: {
      get: (branchId?: string) => get<CatalogView>('/catalog', { branchId }),
      product: (id: string, branchId?: string) => get<ProductView>(`/catalog/products/${encodeURIComponent(id)}`, { branchId }),
      activePromotions: () => get<PromotionView[]>('/promotions/active'),
    },

    orders: {
      quote: (input: QuoteInput) => post<QuoteView>('/orders/quote', input),
      place: (input: PlaceOrderInput) => post<PlaceOrderResult>('/orders', input),
      mine: () => get<OrderView[]>('/orders/mine'),
      get: (id: string) => get<OrderView>(`/orders/${encodeURIComponent(id)}`),
    },

    loyalty: {
      me: () => get<LoyaltyView>('/loyalty/me'),
      redeem: (rewardId: string) => post<LoyaltyView>('/loyalty/redeem', { rewardId }),
      counterCode: () => post<ActivationCodeView>('/loyalty/counter-code'),
    },

    shipper: {
      signupOptions: () => get<ShipperSignupOptions>('/shipper/signup-options'),
      apply: (input: ShipperApplicationInput) => post<ShipperProfileView>('/shipper/applications', input),
      me: () => get<{ profile: ShipperProfileView | null }>('/shipper/me'),
      setOnline: (online: boolean) => post<ShipperProfileView>('/shipper/online', { online }),
      home: () => get<ShipperHomeView>('/shipper/home'),
      history: () => get<OrderView[]>('/shipper/history'),
      earnings: () => get<ShipperEarningsView>('/shipper/earnings'),
      accept: (orderId: string) => post<OrderView>(`/shipper/orders/${encodeURIComponent(orderId)}/accept`),
      release: (orderId: string) => post<OrderView>(`/shipper/orders/${encodeURIComponent(orderId)}/release`),
      pickUp: (orderId: string) => post<OrderView>(`/shipper/orders/${encodeURIComponent(orderId)}/pickup`),
      deliver: (orderId: string, photoUrl: string | null = null) => post<OrderView>(`/shipper/orders/${encodeURIComponent(orderId)}/deliver`, { photoUrl }),
    },

    admin: {
      nav: () => get<AdminNavCounts>('/admin/nav'),
      dashboard: (range: 'today' | '7d' | '30d' = 'today', branchId?: string) => get<DashboardView>('/admin/dashboard', { range, branchId }),

      orders: {
        board: (filter: { q?: string; channel?: string; branchId?: string } = {}) => get<OrderBoardView>('/admin/orders/board', filter),
        incoming: (branchId?: string) => get<OrderView[]>('/admin/orders/incoming', { branchId }),
        get: (id: string) => get<OrderView>(`/admin/orders/${encodeURIComponent(id)}`),
        createAtCounter: (input: CounterOrderInput) => post<PlaceOrderResult>('/admin/orders', input),
        accept: (id: string) => post<OrderView>(`/admin/orders/${encodeURIComponent(id)}/accept`),
        finish: (id: string) => post<OrderView>(`/admin/orders/${encodeURIComponent(id)}/finish`),
        complete: (id: string) => post<OrderView>(`/admin/orders/${encodeURIComponent(id)}/complete`),
        cancel: (id: string, reason: string) => post<OrderView>(`/admin/orders/${encodeURIComponent(id)}/cancel`, { reason }),
        assign: (id: string, shipperId: string | null) => post<OrderView>(`/admin/orders/${encodeURIComponent(id)}/assign`, { shipperId }),
      },

      menu: {
        get: () => get<MenuAdminView>('/admin/menu'),
        create: (input: ProductInput) => post<ProductView>('/admin/products', input),
        update: (id: string, input: ProductInput) => put<ProductView>(`/admin/products/${encodeURIComponent(id)}`, input),
        setVisibility: (id: string, visible: boolean) => post<MenuAdminView>(`/admin/products/${encodeURIComponent(id)}/visibility`, { visible }),
        setStock: (id: string, branchId: string, status: StockStatus) => post<MenuAdminView>(`/admin/products/${encodeURIComponent(id)}/stock`, { branchId, status }),
        createCategory: (name: string) => post<{ id: string; name: string }>('/admin/categories', { name }),
      },

      marketing: {
        settings: () => get<MarketingSettingsView>('/admin/marketing/settings'),
        saveSettings: (input: MarketingSettingsView['editable'] & { apiKey?: { provider: 'claude' | 'openai'; value: string }; clearKey?: 'claude' | 'openai' }) =>
          put<MarketingSettingsView>('/admin/marketing/settings', input),
        testConnection: (provider: 'claude' | 'openai') => post<{ ok: boolean; message: string }>('/admin/marketing/settings/test', { provider }),
        startLogin: (provider: 'claude' | 'openai') => post<MarketingSettingsView>('/admin/marketing/settings/login', { provider }),
        logout: (provider: 'claude' | 'openai') => post<MarketingSettingsView>('/admin/marketing/settings/logout', { provider }),
        usage: () => get<MarketingUsageView>('/admin/marketing/usage'),
        models: (provider: 'claude' | 'openai') => get<AiModelsView>(`/admin/marketing/models/${provider}`),
        conversations: () => get<ConversationSummaryView[]>('/admin/marketing/conversations'),
        conversation: (id: string) => get<ConversationView>(`/admin/marketing/conversations/${encodeURIComponent(id)}`),
        chat: (input: { conversationId: string | null; text: string; provider?: 'claude' | 'openai' | null }, onProgress: (event: AiEventView) => void) =>
          postStream<ChatReplyView, AiEventView>('/admin/marketing/chat', input, onProgress),
        campaigns: () => get<CampaignsView>('/admin/marketing/campaigns'),
        runningCampaigns: () => get<{ code: string; name: string }[]>('/admin/marketing/campaigns/running'),
        campaign: (id: string) => get<CampaignDetailView>(`/admin/marketing/campaigns/${encodeURIComponent(id)}`),
        createCampaign: (input: unknown) => post<CampaignDetailView>('/admin/marketing/campaigns', input),
        updateCampaign: (id: string, input: unknown) => put<CampaignDetailView>(`/admin/marketing/campaigns/${encodeURIComponent(id)}`, input),
        campaignAction: (id: string, action: 'submit' | 'approve' | 'cancel' | 'end') => post<CampaignDetailView>(`/admin/marketing/campaigns/${encodeURIComponent(id)}/action`, { action }),
        addResult: (id: string, input: { channel: string; metric: string; value: number; note: string }) => post<CampaignDetailView>(`/admin/marketing/campaigns/${encodeURIComponent(id)}/results`, input),
        addContent: (id: string, input: { channel: string; kind: string; title: string; body: string }) => post<CampaignDetailView>(`/admin/marketing/campaigns/${encodeURIComponent(id)}/contents`, input),
        updateContent: (id: string, contentId: string, input: { channel: string; kind: string; title: string; body: string }) =>
          put<CampaignDetailView>(`/admin/marketing/campaigns/${encodeURIComponent(id)}/contents/${encodeURIComponent(contentId)}`, input),
        deleteContent: (id: string, contentId: string) => del<CampaignDetailView>(`/admin/marketing/campaigns/${encodeURIComponent(id)}/contents/${encodeURIComponent(contentId)}`),
        summarize: (id: string) => post<CampaignDetailView>(`/admin/marketing/campaigns/${encodeURIComponent(id)}/summarize`),
        publishToFacebook: (id: string, contentId: string, input: { image: { data: string; mimeType: 'image/jpeg' | 'image/png' } | null; scheduleAt: string | null }) =>
          post<CampaignDetailView>(`/admin/marketing/campaigns/${encodeURIComponent(id)}/contents/${encodeURIComponent(contentId)}/facebook`, input),
        facebook: () => get<FacebookSettingsView>('/admin/marketing/facebook'),
        saveFacebook: (input: { pageId: string; token?: string }) => put<FacebookSettingsView>('/admin/marketing/facebook', input),
        clearFacebook: () => del<FacebookSettingsView>('/admin/marketing/facebook'),
      },

      membership: {
        get: () => get<MembershipAdminView>('/admin/membership'),
        saveTiers: (input: { tiers: Tier[]; policy: TierPolicy; moves: Record<string, string> }) => put<MembershipAdminView>('/admin/membership/tiers', input),
        createReward: (input: Omit<Reward, 'id'>) => post<Reward>('/admin/membership/rewards', input),
        updateReward: (id: string, input: Omit<Reward, 'id'>) => put<Reward>(`/admin/membership/rewards/${encodeURIComponent(id)}`, input),
        saveJobs: (jobs: JobsConfig) => put<MembershipAdminView>('/admin/membership/jobs', jobs),
        runJob: (job: JobType, dryRun: boolean) => post<JobRunResult>(`/admin/membership/jobs/${job}/run`, { dryRun }),
        runs: (query: { job?: JobType; before?: string } = {}) => get<JobRunsView>('/admin/membership/runs', query),
        run: (id: string) => get<JobRunDetailView>(`/admin/membership/runs/${encodeURIComponent(id)}`),
      },

      audit: {
        list: (query: { from?: string; to?: string; actorId?: string; group?: string; action?: string; q?: string; before?: number; limit?: number } = {}) =>
          get<AuditLogView>('/admin/audit', query),
        exportCsv: (query: { from?: string; to?: string; actorId?: string; group?: string; action?: string; q?: string } = {}) => download('/admin/audit/export.csv', query),
      },

      counterRedeem: {
        today: () => get<CounterRedemptionsView>('/admin/counter-redeem'),
        lookup: (phone: string, billAmount?: number) => get<CounterRedeemLookupView>('/admin/counter-redeem/lookup', { phone, billAmount }),
        redeem: (input: { branchId: string; phone: string; code: string; billAmount: number; points: number; voucherIds: string[] }) =>
          post<CounterRedeemResult>('/admin/counter-redeem', input),
        void: (id: string) => post<CounterRedemptionsView>(`/admin/counter-redeem/${encodeURIComponent(id)}/void`),
      },

      counterEarn: {
        today: () => get<CounterReceiptsView>('/admin/counter-earn'),
        earn: (input: { branchId: string; phone: string; amount: number; receiptNo?: string; campaignCode?: string }) => post<CounterEarnResult>('/admin/counter-earn', input),
        void: (id: string) => post<CounterReceiptsView>(`/admin/counter-earn/${encodeURIComponent(id)}/void`),
      },

      customers: {
        list: (query: { q?: string; segment?: string } = {}) => get<CustomersAdminView>('/admin/customers', query),
        get: (id: string) => get<CustomerDetailView>(`/admin/customers/${encodeURIComponent(id)}`),
        adjustPoints: (id: string, delta: number, reason: string) => post<CustomerDetailView>(`/admin/customers/${encodeURIComponent(id)}/points`, { delta, reason }),
        setBirthday: (id: string, birthday: string) => put<CustomerDetailView>(`/admin/customers/${encodeURIComponent(id)}/birthday`, { birthday }),
        verify: (id: string, code: string) => post<CustomerDetailView>(`/admin/customers/${encodeURIComponent(id)}/verify`, { code }),
        issueActivationCode: (id: string) => post<ActivationCodeView>(`/admin/customers/${encodeURIComponent(id)}/activation-code`),
        updateRules: (rules: LoyaltyRules) => put<LoyaltyRules>('/admin/loyalty/rules', rules),
        exportCsv: (query: { q?: string; segment?: string } = {}) => download('/admin/customers/export.csv', query),
      },

      shippers: {
        list: () => get<ShippersAdminView>('/admin/shippers'),
        review: (id: string, decision: 'approve' | 'reject' | 'suspend' | 'reinstate', reason = '') => post<ShippersAdminView>(`/admin/shippers/${encodeURIComponent(id)}/review`, { decision, reason }),
        settleCod: (id: string, amount: number) => post<ShippersAdminView>(`/admin/shippers/${encodeURIComponent(id)}/settle-cod`, { amount }),
      },

      promotions: {
        list: () => get<PromotionsAdminView>('/admin/promotions'),
        create: (input: PromotionInput) => post<PromotionView>('/admin/promotions', input),
        update: (id: string, input: PromotionInput) => put<PromotionView>(`/admin/promotions/${encodeURIComponent(id)}`, input),
        toggle: (id: string) => post<PromotionView>(`/admin/promotions/${encodeURIComponent(id)}/toggle`),
      },

      payments: {
        get: () => get<PaymentsAdminView>('/admin/payments'),
        save: (input: { gateways: PaymentsAdminView['gateways']; invoice: PaymentsAdminView['invoice'] }) =>
          put<PaymentsAdminView>('/admin/payments', { gateways: input.gateways.map(({ method, enabled, merchantRef, maxAmount }) => ({ method, enabled, merchantRef, maxAmount })), invoice: input.invoice }),
        testInvoice: () => post<{ ok: true; number: string; lookupUrl: string }>('/admin/payments/test-invoice'),
      },

      staff: {
        employees: () => get<EmployeeView[]>('/admin/staff/employees'),
        addEmployee: (input: { name: string; phone?: string; hourlyRate: number }) => post<EmployeeView>('/admin/staff/employees', input),
        timesheet: (weekStart?: string) => get<TimesheetView>('/admin/staff/timesheet', { weekStart }),
        addShift: (input: { employeeId: string; branchId: string; date: string; start: string; end: string }) =>
          post<{ dayHours: number; allowance: boolean }>('/admin/staff/shifts', input),
        deleteShift: (id: string) => del<void>(`/admin/staff/shifts/${encodeURIComponent(id)}`),
        dayPreview: (employeeId: string, date: string) => get<{ hours: number; byBranch: Record<string, number> }>('/admin/staff/day-preview', { employeeId, date }),
        importIpos: (branchId: string, csv: string) => post<{ imported: number; skipped: { line: number; reason: string }[] }>('/admin/staff/ipos-import', { branchId, csv }),
        payroll: (month?: string) => get<PayrollView>('/admin/staff/payroll', { month }),
        payrollCsv: (month?: string) => download('/admin/staff/payroll.csv', { month }),
      },

      settings: {
        get: () => get<SettingsAdminView>('/admin/settings'),
        createBranch: (input: BranchInput) => post<SettingsAdminView>('/admin/settings/branches', input),
        updateBranch: (id: string, input: BranchInput) => put<SettingsAdminView>(`/admin/settings/branches/${encodeURIComponent(id)}`, input),
        saveDeliveryFees: (bands: DeliveryFeeBand[], shipperKeepsFullFee: boolean) => put<SettingsAdminView>('/admin/settings/delivery-fees', { bands, shipperKeepsFullFee }),
        savePermissions: (matrix: Record<StaffRole, Permission[]>) => put<SettingsAdminView>('/admin/settings/permissions', { matrix }),
        saveApp: (input: { pwa: SettingsAdminView['pwa']; push: SettingsAdminView['push'] }) => put<SettingsAdminView>('/admin/settings/app', input),
        saveAppMode: (mode: AppMode) => put<SettingsAdminView>('/admin/settings/app-mode', mode),
        toggleIntegration: (key: string) => post<SettingsAdminView>(`/admin/settings/integrations/${encodeURIComponent(key)}/toggle`),
        inviteStaff: (input: { name: string; email: string; role: StaffRole; password: string }) => post<SettingsAdminView>('/admin/settings/staff-accounts', input),
      },
    },
  };
};

export type ApiClient = ReturnType<typeof createApiClient>;
